import { useAuth0 } from "@auth0/auth0-react";
import {
	Alert,
	AlertIcon,
	Button,
	Checkbox,
	FormControl,
	FormErrorMessage,
	FormLabel,
	Input,
	Link,
} from "@chakra-ui/react";
import * as EmailValidator from "email-validator";
import { Helmet } from "react-helmet";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import artisanSideView from "src/assets/guest-applicant-signup/artisan-side-view.png";
import { Loading } from "src/components/Loading";
import { Logo } from "src/components/Logo";
import {
	clearGuestApplicantSignupContext,
	readGuestApplicantSignupContext,
	readGuestApplicantSignupContextFromSearch,
	storeGuestApplicantSignupContext,
	GuestApplicantSignupContext,
} from "src/features/guest-applicant-signup/storage";
import { logEvent } from "src/utlis/analytics";

type FieldErrors = {
	email?: string;
	terms?: string;
	auth?: string;
};

const GUEST_APPLICANT_SIGNUP_ROUTE = "/guest-applicant/signup";

const INPUT_PROPS = {
	borderRadius: 0,
	borderColor: "#D7D7D7",
	bg: "white",
	_hover: {
		borderColor: "#C9C9C9",
	},
	_focusVisible: {
		borderColor: "#ED6E5C",
		boxShadow: "0 0 0 1px #ED6E5C",
	},
};

const PRIMARY_BUTTON_PROPS = {
	borderRadius: 0,
	border: "1px solid #ED6E5C",
	bg: "#ED6E5C",
	color: "white",
	boxShadow: "none",
	_hover: { bg: "#D95D4C", borderColor: "#D95D4C" },
	_active: { bg: "#C95242", borderColor: "#C95242" },
};

const normalizeEmail = (value: string) => value.trim().toLowerCase();

const buildTermsRoute = () =>
	`/legal?type=terms&returnTo=${encodeURIComponent(
		GUEST_APPLICANT_SIGNUP_ROUTE
	)}`;

const buildPrivacyRoute = () =>
	`/legal?type=privacy&returnTo=${encodeURIComponent(
		GUEST_APPLICANT_SIGNUP_ROUTE
	)}`;

export default function GuestApplicantSignupPage() {
	const { loginWithRedirect, isAuthenticated, isLoading } = useAuth0();
	const location = useLocation();
	const navigate = useNavigate();
	const [context, setContext] = useState<GuestApplicantSignupContext | null>(null);
	const [email, setEmail] = useState("");
	const [isAgreed, setIsAgreed] = useState(false);
	const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
	const [isSubmitting, setIsSubmitting] = useState(false);
	const hasTrackedPageView = useRef(false);

	const termsRoute = useMemo(buildTermsRoute, []);
	const privacyRoute = useMemo(buildPrivacyRoute, []);

	useEffect(() => {
		const urlContext = readGuestApplicantSignupContextFromSearch(location.search);

		if (urlContext) {
			const nextContext = storeGuestApplicantSignupContext(urlContext);

			setContext(nextContext);
			setEmail(nextContext.email);

			if (location.search) {
				navigate(GUEST_APPLICANT_SIGNUP_ROUTE, { replace: true });
			}

			return;
		}

		const storedContext = readGuestApplicantSignupContext();

		if (storedContext.status === "valid") {
			setContext(storedContext.context);
			setEmail(storedContext.context.email);

			return;
		}

		if (storedContext.status === "expired") {
			logEvent("guest_applicant_signup_context_expired");
		}

		navigate("/login", { replace: true });
	}, [location.search, navigate]);

	useEffect(() => {
		if (!context || hasTrackedPageView.current) {
			return;
		}

		logEvent("guest_applicant_signup_page_viewed", {
			job_id: context.jobId,
		});
		hasTrackedPageView.current = true;
	}, [context]);

	useEffect(() => {
		if (!context || isLoading || !isAuthenticated) {
			return;
		}

		clearGuestApplicantSignupContext();
		navigate("/apply", { replace: true });
	}, [context, isAuthenticated, isLoading, navigate]);

	const startAuthFlow = async ({
		mode,
	}: {
		mode: "signup" | "login";
	}) => {
		if (!context) {
			return;
		}

		const normalizedEmail = normalizeEmail(email);
		const nextFieldErrors: FieldErrors = {};

		if (!normalizedEmail) {
			nextFieldErrors.email = "Enter your email address to continue.";
		} else if (!EmailValidator.validate(normalizedEmail)) {
			nextFieldErrors.email = "Enter a valid email address.";
		}

		if (mode === "signup" && !isAgreed) {
			nextFieldErrors.terms =
				"You must agree to the terms and privacy policy to continue.";
		}

		setFieldErrors(nextFieldErrors);

		if (Object.keys(nextFieldErrors).length > 0) {
			return;
		}

		setIsSubmitting(true);

		const eventName =
			mode === "signup"
				? "guest_applicant_signup_create_account_clicked"
				: "guest_applicant_signup_login_clicked";

		logEvent(eventName, {
			job_id: context.jobId,
		});

		try {
			window.localStorage.setItem("redirect", "/apply");
			clearGuestApplicantSignupContext();

			await loginWithRedirect({
				login_hint: normalizedEmail,
				...(mode === "signup" ? { screen_hint: "signup" } : {}),
			});
		} catch {
			storeGuestApplicantSignupContext({
				email: normalizedEmail,
				jobId: context.jobId,
				jobTitle: context.jobTitle,
				jobLocation: context.jobLocation,
			});
			setFieldErrors({
				auth: "We couldn't start authentication. Please try again.",
			});
			setIsSubmitting(false);
		}
	};

	if (!context || isLoading) {
		return <Loading />;
	}

	return (
		<>
			<Helmet>
				<title>Create your account | LaborHack Pro</title>
			</Helmet>
			<div className="min-h-screen bg-[#F9F5F3] md:bg-white">
				<div className="grid min-h-screen grid-cols-1 md:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)]">
					<div className="relative hidden overflow-hidden md:flex md:min-h-screen">
						<img
							src={artisanSideView}
							alt="LaborHack artisan"
							className="absolute inset-0 h-full w-full object-cover"
						/>
						<div className="absolute inset-0 bg-[#161616]/30" />
						<div className="relative z-10 my-auto w-full bg-gradient-to-t from-[#161616]/78 via-[#161616]/48 to-transparent px-10 pb-12 pt-40 text-white">
							<div className="max-w-xl">
								<h1 className="text-5xl font-bold leading-[1.02]">
									You&apos;ve taken the first step.
								</h1>
								<p className="mt-5 max-w-lg text-lg leading-8 text-white/90">
									Your application has been submitted. Join LaborHack to get
									matched with more jobs in the future.
								</p>
								<div className="mt-8 border border-white/20 bg-white/14 p-5 backdrop-blur-[10px]">
									<p className="text-xs font-semibold uppercase tracking-[0.24em] text-white/65">
										Applied for
									</p>
									<p className="mt-3 text-2xl font-semibold leading-8 text-white">
										{context.jobTitle}{" "}
										<span className="text-white/80">{"\u2014"} </span>
										<span className="text-white/80">{context.jobLocation}</span>
									</p>
								</div>
							</div>
						</div>
					</div>
					<div className="flex min-h-screen w-full items-stretch justify-center px-4 py-8 sm:px-6 md:bg-white md:px-10 md:py-0">
						<div className="flex w-full max-w-[520px] flex-col justify-center">
							<div className="bg-white px-6 py-7 sm:px-8 sm:py-10 md:px-0 md:py-0">
								<div className="md:hidden">
									<Logo fill="#ED6E5C" width={148} />
								</div>
								<div className="mt-6 md:hidden">
									<h1 className="text-[2rem] font-bold leading-10 text-[#1E2238]">
										You&apos;ve taken the first step.
									</h1>
									<p className="mt-3 text-base leading-7 text-[#6E6E6E]">
										Your application has been submitted. Join LaborHack to get
										matched with more jobs in the future.
									</p>
									<div className="mt-6 border border-[#E7DAD4] bg-[#FBF7F4] px-5 py-4">
										<p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8B8B8B]">
											Applied for
										</p>
										<p className="mt-2 text-lg font-semibold leading-7 text-[#1E2238]">
											{context.jobTitle}{" "}
											<span className="text-[#7C7C7C]">{"\u2014"} </span>
											<span className="text-[#4B4B4B]">
												{context.jobLocation}
											</span>
										</p>
									</div>
								</div>

								<div className="mt-8 border-t border-[#EFE7E2] pt-8 md:mt-0 md:border-t-0 md:pt-0">
									<h2 className="text-3xl font-bold text-[#1E2238]">
										Create your account
									</h2>
									<p className="mt-2 text-base text-[#7B7B7B]">
										Complete signup to get matched with jobs
									</p>

									<form
										className="mt-8 space-y-6"
										onSubmit={(event) => {
											event.preventDefault();
											void startAuthFlow({ mode: "signup" });
										}}
									>
										<FormControl isInvalid={Boolean(fieldErrors.email)}>
											<FormLabel
												fontSize="md"
												fontWeight="600"
												color="#3B3B3B"
												mb={2}
											>
												Email address
											</FormLabel>
											<Input
												type="email"
												value={email}
												onChange={(event) => {
													setEmail(event.target.value);

													if (fieldErrors.email || fieldErrors.auth) {
														setFieldErrors((currentErrors) => ({
															...currentErrors,
															email: undefined,
															auth: undefined,
														}));
													}
												}}
												size="lg"
												{...INPUT_PROPS}
											/>
											<FormErrorMessage>{fieldErrors.email}</FormErrorMessage>
										</FormControl>

										<FormControl isInvalid={Boolean(fieldErrors.terms)}>
											<Checkbox
												colorScheme="brand"
												isChecked={isAgreed}
												onChange={(event) => {
													setIsAgreed(event.target.checked);

													if (fieldErrors.terms) {
														setFieldErrors((currentErrors) => ({
															...currentErrors,
															terms: undefined,
														}));
													}
												}}
												alignItems="flex-start"
												sx={{
													".chakra-checkbox__control": {
														mt: "6px",
													},
												}}
											>
												<span className="text-base leading-7 text-[#5C5C5C]">
													I agree to the LaborHack{" "}
													<Link
														href={termsRoute}
														target="_blank"
														rel="noreferrer"
														color="#ED6E5C"
														onClick={(event) => {
															event.stopPropagation();
															logEvent("guest_applicant_signup_terms_opened", {
																job_id: context.jobId,
															});
														}}
													>
														Terms of Service
													</Link>{" "}
													and{" "}
													<Link
														href={privacyRoute}
														target="_blank"
														rel="noreferrer"
														color="#ED6E5C"
														onClick={(event) => {
															event.stopPropagation();
															logEvent(
																"guest_applicant_signup_privacy_opened",
																{
																	job_id: context.jobId,
																}
															);
														}}
													>
														Privacy Policy
													</Link>
												</span>
											</Checkbox>
											<FormErrorMessage>{fieldErrors.terms}</FormErrorMessage>
										</FormControl>

										{fieldErrors.auth ? (
											<Alert status="error" borderRadius={0}>
												<AlertIcon />
												{fieldErrors.auth}
											</Alert>
										) : null}

										<Button
											type="submit"
											isLoading={isSubmitting}
											loadingText="Creating account"
											size="lg"
											w="full"
											{...PRIMARY_BUTTON_PROPS}
										>
											Create account
										</Button>
									</form>

									<p className="mt-6 text-center text-base text-[#8A8A8A]">
										Already have an account?{" "}
										<button
											type="button"
											className="font-semibold text-[#ED6E5C]"
											onClick={() => {
												void startAuthFlow({ mode: "login" });
											}}
											disabled={isSubmitting}
										>
											Log in
										</button>
									</p>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</>
	);
}
